import { Progress } from '@backstage/core-components';
import { configApiRef, useApi } from '@backstage/frontend-plugin-api';
import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import {
  clearLegacyGuestFallbackState,
  clearStoredGuestSelection,
  hasGuestProvider,
  hasGuestSessionHint,
  hasVisibleFetchError,
  isBrowser,
  isGuestProviderSelected,
  markGuestSessionActive,
} from './guestAuth';

const GUEST_REFRESH_COOLDOWN_MS = 15_000;
const REPAIR_RELOAD_COOLDOWN_MS = 5_000;

export const GuestAuthSessionWrapper = ({
  children,
}: {
  children: ReactNode;
}) => {
  const configApi = useApi(configApiRef);
  const backendBaseUrl = configApi
    .getString('backend.baseUrl')
    .replace(/\/$/, '');
  const guestProviderEnabled = hasGuestProvider(configApi);
  const [ready, setReady] = useState(
    () =>
      !(
        guestProviderEnabled &&
        (isGuestProviderSelected() || hasGuestSessionHint())
      ),
  );
  const refreshInFlightRef = useRef<Promise<boolean> | null>(null);
  const lastRefreshAtRef = useRef(0);
  const lastRepairAtRef = useRef(0);

  const shouldBootstrapGuestSession = useCallback(
    () =>
      guestProviderEnabled &&
      (isGuestProviderSelected() || hasGuestSessionHint()),
    [guestProviderEnabled],
  );

  const shouldRepairGuestSession = useCallback(
    () =>
      guestProviderEnabled &&
      (isGuestProviderSelected() ||
        hasGuestSessionHint() ||
        hasVisibleFetchError()),
    [guestProviderEnabled],
  );

  const refreshGuestSession = useCallback(
    async (force = false): Promise<boolean> => {
      if (!guestProviderEnabled) {
        return false;
      }

      const now = Date.now();
      if (refreshInFlightRef.current) {
        return refreshInFlightRef.current;
      }
      if (
        !force &&
        now - lastRefreshAtRef.current < GUEST_REFRESH_COOLDOWN_MS
      ) {
        return true;
      }

      const refreshPromise = (async () => {
        try {
          const response = await window.fetch(
            `${backendBaseUrl}/api/auth/guest/refresh`,
            {
              credentials: 'include',
            },
          );
          if (!response.ok) {
            return false;
          }

          const payload = await response.json().catch(() => undefined);
          if (!payload?.backstageIdentity?.token) {
            return false;
          }

          markGuestSessionActive();
          lastRefreshAtRef.current = Date.now();
          return true;
        } catch {
          return false;
        } finally {
          refreshInFlightRef.current = null;
        }
      })();

      refreshInFlightRef.current = refreshPromise;
      return refreshPromise;
    },
    [backendBaseUrl, guestProviderEnabled],
  );

  useEffect(() => {
    clearLegacyGuestFallbackState();

    let cancelled = false;
    async function bootstrapGuestSession() {
      if (!shouldBootstrapGuestSession()) {
        if (!cancelled) {
          setReady(true);
        }
        return;
      }

      const ok = await refreshGuestSession(true);
      if (cancelled) {
        return;
      }

      if (!ok) {
        clearStoredGuestSelection();
        window.location.reload();
        return;
      }

      setReady(true);
    }

    void bootstrapGuestSession();
    return () => {
      cancelled = true;
    };
  }, [backendBaseUrl, refreshGuestSession, shouldBootstrapGuestSession]);

  useEffect(() => {
    if (!ready || !isBrowser()) {
      return undefined;
    }

    const maybeRepairGuestSession = async () => {
      if (!shouldRepairGuestSession() || !hasVisibleFetchError()) {
        return;
      }

      const ok = await refreshGuestSession(true);
      if (!ok) {
        clearStoredGuestSelection();
        window.location.reload();
        return;
      }

      if (!hasVisibleFetchError()) {
        return;
      }

      const now = Date.now();
      if (now - lastRepairAtRef.current < REPAIR_RELOAD_COOLDOWN_MS) {
        return;
      }

      lastRepairAtRef.current = now;
      window.location.reload();
    };

    void maybeRepairGuestSession();

    const handleFocus = () => {
      void maybeRepairGuestSession();
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void maybeRepairGuestSession();
      }
    };
    const observer = new MutationObserver(() => {
      void maybeRepairGuestSession();
    });
    const timer = window.setInterval(() => {
      void maybeRepairGuestSession();
    }, 2_000);

    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    observer.observe(document.body, {
      subtree: true,
      childList: true,
      characterData: true,
    });

    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      observer.disconnect();
      window.clearInterval(timer);
    };
  }, [ready, refreshGuestSession, shouldRepairGuestSession]);

  if (!ready) {
    return <Progress />;
  }

  return <>{children}</>;
};
